import { effect, OnDestroy, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LayoutService } from './service/app.layout.service';
import { BackofficeSessionService } from '../../backoffice-session.service';
import type { MenuItem } from 'primeng/api';

/** Keeps the icon markup readable next to the labels it belongs to. */
const icon = (name: string) => `<span class="material-symbols-outlined text-[16px]">${name}</span>`;

/**
 * Routes that carry query parameters or child segments need the active state to
 * survive them, otherwise the entry stops looking selected as soon as a filter
 * or a detail view is opened.
 */
const KEEP_ACTIVE_ON_CHILDREN = {
  paths: 'subset',
  queryParams: 'ignored',
  matrixParams: 'ignored',
  fragment: 'ignored',
};

@Component({
  selector: 'bo-menu',
  templateUrl: './app.menu.component.html',
  standalone: false,
})
export class AppMenuComponent implements OnInit, OnDestroy {
  model: MenuItem[] = [];

  private languageSubscription?: Subscription;

  constructor(
    public layoutService: LayoutService,
    private readonly translate: TranslateService,
    private readonly session: BackofficeSessionService,
  ) {
    // Rebuild when the roles resolve, so the superadmin-only entry appears
    // once the session lands rather than only after a language switch.
    effect(() => {
      this.session.roles();
      this.buildModel();
    });
  }

  ngOnInit() {
    this.buildModel();
    // Labels are plain interpolations in the item template, so the model is
    // rebuilt on a language switch rather than making every entry reactive.
    this.languageSubscription = this.translate.onLangChange.subscribe(() => this.buildModel());
  }

  ngOnDestroy() {
    this.languageSubscription?.unsubscribe();
  }

  private buildModel() {
    const t = (key: string) => this.translate.instant(key);
    // Grouped by business domain rather than by page type: someone looking into a
    // payment question should find every payment screen in one place. Every route
    // and label from the previous layout is kept, only the grouping changed.
    this.model = [
      {
        label: t('menu.steering'),
        items: [
          {
            label: t('menu.synthesis'),
            icon: icon('insights'),
            routerLink: ['/synthese'],
          },
          {
            label: t('menu.todo'),
            icon: icon('task_alt'),
            routerLink: ['/a-traiter'],
          },
          {
            label: t('menu.studio'),
            icon: icon('campaign'),
            routerLink: ['/studio'],
          },
          {
            label: t('menu.userActivity'),
            icon: icon('activity_zone'),
            routerLink: ['/user-activity'],
          },
          {
            label: t('menu.userAnalytics'),
            icon: icon('bar_chart'),
            routerLink: ['/report'],
          },
          {
            label: t('menu.segments'),
            icon: icon('groups_3'),
            routerLink: ['/segments'],
          },
          {
            label: t('menu.fillRate'),
            icon: icon('donut_large'),
            routerLink: ['/remplissage'],
          },
          {
            label: t('menu.acquisition'),
            icon: icon('person_add'),
            routerLink: ['/acquisition'],
          },
          {
            label: t('menu.activation'),
            icon: icon('how_to_reg'),
            routerLink: ['/activation'],
          },
          {
            label: t('menu.matching'),
            icon: icon('hub'),
            routerLink: ['/appariement'],
          },
          {
            label: t('menu.journeyFunnel'),
            icon: icon('conversion_path'),
            routerLink: ['/parcours'],
          },
          {
            label: t('menu.prizeDraw'),
            icon: icon('card_giftcard'),
            routerLink: ['/jeu-concours'],
          },
          {
            label: t('menu.needingHelp'),
            icon: icon('detector_alarm'),
            routerLink: ['/activites-en-difficulte'],
          },
          {
            label: t('menu.heatmap'),
            icon: icon('map'),
            routerLink: ['/carte-de-chaleur'],
          },
          {
            label: t('menu.stores'),
            icon: icon('smartphone'),
            routerLink: ['/stores'],
          },
          {
            label: t('menu.crossAnalysis'),
            icon: icon('grid_view'),
            routerLink: ['/analyse-croisee'],
          },
          {
            label: t('menu.citiesToActivate'),
            icon: icon('location_city'),
            routerLink: ['/villes-a-activer'],
          },
          {
            label: t('menu.duplicateCities'),
            icon: icon('pin_drop'),
            routerLink: ['/villes-en-doublon'],
          },
          {
            label: t('menu.eventRevenue'),
            icon: icon('chart_data'),
            routerLink: ['/event-revenue'],
          },
        ],
      },
      {
        label: t('menu.users'),
        items: [
          {
            label: t('menu.userLists'),
            icon: icon('id_card'),
            routerLink: ['/users'],
          },
          {
            label: t('menu.usersManagement'),
            icon: icon('manage_accounts'),
            routerLink: ['/users-management'],
          },
          {
            label: t('menu.groups'),
            icon: icon('groups'),
            routerLink: ['/groups'],
          },
          {
            // SMA-90: the brand-safety review queue, next to groups since that
            // is what it moderates.
            label: t('menu.groupReview'),
            icon: icon('verified'),
            routerLink: ['/validation-groupes'],
          },
          {
            // With the members rather than in a section of its own: a report
            // is about a person, and whoever opens it ends up on their file.
            label: t('menu.memberReports'),
            icon: icon('flag'),
            routerLink: ['/signalements'],
          },
          {
            // Next to the reports: both are a pile of members to look at.
            label: t('menu.collusionCases'),
            icon: icon('group'),
            routerLink: ['/ententes'],
          },
        ],
      },
      {
        label: t('menu.activities'),
        items: [
          {
            label: t('menu.futureActivities'),
            icon: icon('calendar_add_on'),
            routerLink: ['/events'],
          },
          {
            label: t('menu.pastActivities'),
            icon: icon('event_available'),
            routerLink: ['/past-events'],
          },
          {
            label: t('menu.activitiesManagement'),
            icon: icon('calendar_month'),
            routerLink: ['/activities'],
          },
          {
            label: t('menu.paidEventAccess'),
            icon: icon('key'),
            routerLink: ['/paid-events'],
          },
        ],
      },
      {
        label: t('menu.gamification'),
        items: [
          {
            // The finals the team enters each year: each one's week is a badge.
            label: t('menu.finals'),
            icon: icon('trophy'),
            routerLink: ['/competitions'],
          },
          {
            // The radius of the presence check, changed without a new version of the API.
            label: t('menu.gamificationSettings'),
            icon: icon('tune'),
            routerLink: ['/reglages-gamification'],
          },
          {
            // Premium: the brands the team makes partner events with, and the
            // form to write one (decided with Maude on 15 October 2026).
            label: t('menu.brands'),
            icon: icon('storefront'),
            routerLink: ['/marques'],
          },
          {
            label: t('menu.partnerEvent'),
            icon: icon('workspace_premium'),
            routerLink: ['/evenement-partenaire'],
          },
          {
            label: t('menu.partnerEvents'),
            icon: icon('event_note'),
            routerLink: ['/evenements-partenaires'],
          },
        ],
      },
      {
        label: t('menu.payments'),
        items: [
          {
            label: t('menu.monetisation'),
            icon: icon('monitoring'),
            routerLink: ['/monetisation'],
          },
          {
            label: t('menu.transactions'),
            icon: icon('directory_sync'),
            routerLink: ['/transactions'],
            routerLinkActiveOptions: KEEP_ACTIVE_ON_CHILDREN,
          },
          {
            label: t('menu.withdrawalRequest'),
            icon: icon('output'),
            routerLink: ['/payout-request'],
          },
          {
            label: t('menu.organizerBalances'),
            icon: icon('account_balance_wallet'),
            routerLink: ['/organizer-balances'],
            routerLinkActiveOptions: KEEP_ACTIVE_ON_CHILDREN,
          },
          // Reading a member's balance and moving it is superadmin-only, like
          // access management: the API refuses anybody else, so offering the
          // entry to an ordinary admin would only lead them to a wall of 403s.
          ...(this.session.isSuperadmin()
            ? [
                {
                  label: t('menu.clientWallets'),
                  icon: icon('wallet'),
                  routerLink: ['/client-wallet'],
                },
              ]
            : []),
          {
            // Next to the balances rather than in its own section: somebody
            // looking at what organisers hold is one click from discovering
            // that part of it cannot be paid.
            label: t('menu.blockedPayouts'),
            icon: icon('money_off'),
            routerLink: ['/paiements-bloques'],
          },
          {
            label: t('menu.earningsStatement'),
            icon: icon('receipt_long'),
            routerLink: ['/earnings-statement'],
          },
          {
            label: t('menu.commissionsRate'),
            icon: icon('percent'),
            routerLink: ['/commission'],
          },
        ],
      },
      {
        label: t('menu.compliance'),
        items: [
          {
            label: t('menu.termsContent'),
            icon: icon('contract'),
            routerLink: ['/legal'],
          },
          {
            label: t('menu.termsAcceptances'),
            icon: icon('gavel'),
            routerLink: ['/cgv-acceptance'],
          },
          {
            label: t('menu.auditLogs'),
            icon: icon('policy'),
            routerLink: ['/audit-logs'],
          },
        ],
      },
      {
        label: t('menu.administration'),
        items: [
          {
            label: t('menu.deletedItems'),
            icon: icon('delete'),
            routerLink: ['/deleted-items'],
          },
          // Managing who can reach the back office is a superadmin-only screen.
          ...(this.session.isSuperadmin()
            ? [
                {
                  label: t('menu.accessManagement'),
                  icon: icon('admin_panel_settings'),
                  routerLink: ['/access-management'],
                },
              ]
            : []),
        ],
      },
    ];
  }
}
